/**
 * Shared formatters for rendering backend UTC instants in the viewer's local
 * timezone. `Intl.DateTimeFormat` is expensive to construct, so each variant
 * is built once and reused — pass a value through {@link parseApiDateTime}
 * first, then one of these, rather than calling `.toLocaleString(...)` inline.
 */
const GB_DATE = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
const GB_TIME_SECONDS = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
});
const GB_DATETIME = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
});
const GB_SHORT_DATETIME = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});
const GB_LONG_DATETIME = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
});

export const toDateKey = (d?: Date | string | null) => {
    if (!d) return "";
    const dateObj =
        typeof d === "string"
            ? (parseApiDateTime(d) ?? new Date(d))
            : d;
    if (Number.isNaN(dateObj.getTime())) return "";
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
};

export const startOfLocalDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Start of `date`'s local calendar day, as the UTC instant the API expects. */
export function toDiaryApiRangeStart(date: Date): string {
    return startOfLocalDay(date).toISOString();
}

/** End of `date`'s local calendar day, as the UTC instant the API expects. */
export function toDiaryApiRangeEnd(date: Date): string {
    const d = startOfLocalDay(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
}

/** True when `date` falls on a calendar day within [rangeStart, rangeEnd] (inclusive, local). */
export function isLocalDateInRange(date: Date, rangeStart: Date, rangeEnd: Date): boolean {
    const t = startOfLocalDay(date).getTime();
    return (
        t >= startOfLocalDay(rangeStart).getTime() &&
        t <= startOfLocalDay(rangeEnd).getTime()
    );
}

/** True when `date` is strictly before today (local calendar day). */
export function isBeforeToday(date: Date): boolean {
    return startOfLocalDay(date).getTime() < startOfLocalDay(new Date()).getTime();
}

export function isToday(date: Date): boolean {
    return toDateKey(date) === toDateKey(new Date());
}

export function isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return toDateKey(date) === toDateKey(yesterday);
}

/** True when `date` is strictly before yesterday (local calendar day). */
export function isBeforeYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return startOfLocalDay(date).getTime() < startOfLocalDay(yesterday).getTime();
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Parse a backend datetime string (UTC, e.g. `2026-09-11T14:30:00Z`) into a
 * `Date`. `Date` already reads the `Z`/offset as UTC and every getter
 * (`getHours()`, `getDate()`, ...) reports it back in the viewer's local
 * timezone, so no manual shifting is needed here.
 */
export function parseApiDateTime(value: string | null | undefined): Date | null {
    if (!value?.trim()) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Value for `<input type="datetime-local" />` using wall-clock components from API strings. */
export function toDatetimeLocalValue(d: Date | string | null | undefined): string {
    if (d == null || d === "") return "";
    const dateObj = typeof d === "string" ? parseApiDateTime(d) : d;
    if (!dateObj || Number.isNaN(dateObj.getTime())) return "";
    return `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}-${pad2(dateObj.getDate())}T${pad2(dateObj.getHours())}:${pad2(dateObj.getMinutes())}`;
}

/** Today at 00:00 as a `datetime-local` string — a `min` that bars past days but no time of today. */
export function todayDatetimeLocalMin(): string {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T00:00`;
}

/** Format time for calendar pills (HH:mm, local wall-clock from API string). */
export function formatCalendarTime(value: string | null | undefined): string {
    if (!value) return "";
    const d = parseApiDateTime(value);
    if (!d) return "";
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Convert a `datetime-local` input value (local wall-clock) to a UTC ISO string for the API. */
export function fromDatetimeLocalToApi(value: string): string | null {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    const [datePart, timePart] = trimmed.split("T");
    if (!datePart || !timePart) return null;
    const [y, mo, da] = datePart.split("-").map(Number);
    const [h, m] = timePart.split(":").map(Number);
    if ([y, mo, da, h, m].some((n) => Number.isNaN(n)) || h < 0 || h > 23 || m < 0 || m > 59) return null;
    const local = new Date(y, mo - 1, da, h, m);
    return Number.isNaN(local.getTime()) ? null : local.toISOString();
}

/** @deprecated Use {@link fromDatetimeLocalToApi} for diary API payloads. */
export function fromDatetimeLocalToISO(value: string): string | null {
    return fromDatetimeLocalToApi(value);
}

/** Default 9:00–9:30 slot on a calendar day (local time). */
export function defaultDiarySlotForDate(date: Date): { start: string; end: string } {
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(date);
    end.setHours(9, 30, 0, 0);
    return {
        start: toDatetimeLocalValue(start),
        end: toDatetimeLocalValue(end),
    };
}

export function formatDateAbsolute(dateString: string | null | undefined): string {
    const d = parseApiDateTime(dateString);
    return d ? GB_DATETIME.format(d) : "—";
}

export function formatEnquiryDateParts(dateString: string | null | undefined): { date: string; time: string } | null {
    const d = parseApiDateTime(dateString);
    if (!d) return null;
    return { date: `${GB_DATE.format(d)},`, time: GB_TIME_SECONDS.format(d) };
}

/** `dd/mm/yyyy` in the viewer's local timezone, for compact date-only display. */
export function formatLocalDate(dateString: string | null | undefined): string {
    const d = parseApiDateTime(dateString);
    return d ? GB_DATE.format(d) : "";
}

/** `d Mon yyyy, HH:mm` in the viewer's local timezone, e.g. for note/activity timestamps. */
export function formatShortDateTime(dateString: string | null | undefined): string {
    const d = parseApiDateTime(dateString);
    return d ? GB_SHORT_DATETIME.format(d) : "";
}

/** `Weekday d Month yyyy, h:mm am/pm` in the viewer's local timezone, e.g. for appointment copy. */
export function formatLongDateTime(dateString: string | null | undefined): string {
    const d = parseApiDateTime(dateString);
    return d ? GB_LONG_DATETIME.format(d) : "";
}

